import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/auth-context";
import {
  FiBarChart2,
  FiClipboard,
  FiCreditCard,
  FiHelpCircle,
  FiLogOut,
  FiMail,
  FiMessageSquare,
  FiSettings,
  FiShare2,
  FiUser,
  FiClock,
} from "react-icons/fi";

export interface NavLeaf {
  name: string;
  to: string;
}

export interface NavItem {
  name: string;
  icon?: React.ReactNode;
  to?: string;
  subMenus?: NavLeaf[];
  isAdminGroup?: boolean;
  isLogout?: boolean;
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

/**
 * Sidebar navigation, grouped into sections. Routes, labels and the
 * role-based visibility rules are the same as the original flat menu.
 */
export const useNavSections = (): NavSection[] => {
  const { t } = useTranslation();
  const { auth, roleLoading } = useAuth();
  const isAdmin = Number(auth?.role) === 2;

  const sections: NavSection[] = [
    {
      heading: "Overview",
      items: [
        { name: t("side.bar.Dashboard"), icon: <FiBarChart2 />, to: "/Dashboard" },
        {
          name: t("side.bar.Surveys"),
          icon: <FiClipboard />,
          subMenus: [
            { name: t("side.bar.Best"), to: "/my-surveys" },
            { name: t("side.bar.Short"), to: "/short-survey" },
            { name: t("side.bar.Most"), to: "/most-point" },
          ],
        },
      ],
    },
    {
      heading: "Account",
      items: [
        { name: t("side.bar.Profile"), icon: <FiUser />, to: "/my-profile" },
        { name: t("side.bar.Account"), icon: <FiSettings />, to: "/accountsection" },
        { name: t("side.bar.Friend"), icon: <FiShare2 />, to: "/my-referral" },
      ],
    },
    {
      heading: "Activity",
      items: [
        { name: t("side.bar.History"), icon: <FiClock />, to: "/history" },
        { name: "Wallet History", icon: <FiCreditCard />, to: "/wallet-history" },
      ],
    },
  ];

  if (isAdmin) {
    sections.push({ heading: "Administration", items: [{ name: "Admin", isAdminGroup: true }] });
  }

  const supportItems: NavItem[] = [];
  if (!roleLoading && !isAdmin) {
    supportItems.push({ name: "My queries", icon: <FiMessageSquare />, to: "/my-queries" });
  }
  supportItems.push(
    { name: t("side.bar.Help"), icon: <FiHelpCircle />, to: "/raise-query" },
    { name: t("side.bar.Unsubscribe"), icon: <FiMail />, to: "/unsubscribe" },
    { name: t("side.bar.Logout"), icon: <FiLogOut />, isLogout: true }
  );
  sections.push({ heading: "Support", items: supportItems });

  return sections;
};

/** Extra page labels for routes that are not in the sidebar. */
const extraTitles: Record<string, string> = {
  "/admin/users": "User Management",
  "/admin/user-queries": "User Queries",
  "/survey-redeem": "Points Balance",
  "/virtual-wallet": "Virtual Wallet",
  "/email-preferences": "Email Preferences",
  "/my-profile-category-question": "Profile Questions",
  "/alert": "Notifications",
  "/more": "More",
};

/** Resolves the section and page label for the current path (topbar breadcrumb). */
export const findNavLabel = (sections: NavSection[], pathname: string) => {
  const path = pathname.replace(/\/+$/, "").toLowerCase();
  for (const section of sections) {
    for (const item of section.items) {
      if (item.to && item.to.toLowerCase() === path) return { section: section.heading, page: item.name };
      const leaf = item.subMenus?.find((sub) => sub.to.toLowerCase() === path);
      if (leaf) return { section: item.name, page: leaf.name };
    }
  }
  const extra = Object.keys(extraTitles).find((key) => key === path || path.startsWith(`${key}/`));
  if (extra) return { section: "Workspace", page: extraTitles[extra] };
  if (path.startsWith("/redeem")) return { section: "Rewards", page: "Redeem" };
  return null;
};
